import React, { useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';

import goodApi from '@mo-apis/good/goodApi';
import { PriceUtils } from '@ecp/common/src/utils/price/PriceUtils';
import { resolvePriceOpen } from '@ecp/common/src/utils/good/goodUtil';
import useOptionCountValidator from '@ecp/common/src/hooks/good/option/useGoodOptionValidator';
import moTheme from '@ecp/common/src/style/theme/mo';
import { overrideProperties } from '@ecp/common/src/style/utils';
import { paddingPropTypes, sizePropTypes } from '@ecp/common/src/style/propTypes';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import NumberIndicator from '@ecp/common/src/components/input/mo/NumberIndicator';
import { ReactComponent as ClearSvg } from '@ecp/common/src/assets/icon/tooltip__close.svg';

import { goodOptionListState } from '@mo-recoil/good/atom';
import { useAlertDialog } from '@mo-hooks/common/useModal';
import useSiteInfo from '@mo-hooks/common/useSiteInfo';

const OptionPriceUnitInfo = ({ title }) => {
  const theme = useTheme();
  return (
  <FlexBoxColumn align-items={'flex-end'} width={'100%'}>
    <Spacing top={8} />
    <TextBox color={theme.color.text.guide} size={moTheme.font.size.$12} height={'12px'}>
      {title}
    </TextBox>
  </FlexBoxColumn>
)};

OptionPriceUnitInfo.propTypes = {
  title: PropTypes.string,
};

const GoodSelectedOption = ({ 
  option, 
  isGroup, 
  isRental, 
  isRegularDelivery, 
  stockQtyDisplay, 
  showBtnBulkp,
  selectedOptionList,
  ordPossibleMaxQtyStandardCode,
  ordPossibleMaxQty,
  ordPossibleMinQty,
  ordPossibleMaxAmount,
  ordPossibleMaxFormattedAmount,
  ordPossibleMinAmount,
  ordPossibleMinFormattedAmount,
  bulkpOrdPossibleMinQty, }) => {
  const theme = useTheme();
  const priceUtils = new PriceUtils();
  const { isPriceOpen: siteIsPriceOpen, priceFormat } = useSiteInfo();
  // REQ_DS_05_01
  const isPriceOpen = resolvePriceOpen(option?.priceExpsUseYn, siteIsPriceOpen);
  const setOptionList = useSetRecoilState(goodOptionListState);
  const { showAlertDialog } = useAlertDialog();
  const { optionCountValidator } = useOptionCountValidator(showAlertDialog);
  const [ alertMessage, setAlertMessage ] = useState('');

  const handleCountChange = async (e, option) => {
    const newCount = Number(e?.target?.value);

    const selectedItemList = selectedOptionList?.map((opt) => ({
      goodNumber: opt?.goodNumber,
      itemNumber: opt?.itemNumber,
      orderQuantity: (opt.itemNumber === option?.itemNumber && opt.goodNumber === option?.goodNumber) ? newCount : opt?.count,
      orderAmount: opt?.discountPrice * ((opt.itemNumber === option?.itemNumber && opt.goodNumber === option?.goodNumber) ? newCount : opt?.count),
      additionalItem: opt?.additionalItem,
      discountPrice: opt?.discountPrice,
      discountFormattedPrice: opt?.discountFormattedPrice,
    }));
    console.log(selectedItemList);
    const { result: availableResult = [] } = await goodApi.checkOrderAvailable(selectedItemList);
    const { message = '', rollback = false, reAlert = true } = optionCountValidator({
      option,
      newCount,
      showBtnBulkp, 
      availableResult,
      selectedOptionList: selectedItemList,
      ordPossibleMaxQtyStandardCode,
      ordPossibleMaxQty,
      ordPossibleMinQty,
      ordPossibleMaxAmount,
      ordPossibleMaxFormattedAmount,
      ordPossibleMinAmount,
      ordPossibleMinFormattedAmount,
      bulkpOrdPossibleMinQty,
      priceFormat,
    });

    console.log(message, rollback)
    if (message) {
      if (!(message === alertMessage && !reAlert)) { //직전 노출된 알럿과 다르거나, 같아도 reAlert가 true인 경우 알럿 노출 
        showAlertDialog(message);
        setAlertMessage(message);
      }

      if (rollback) {
        return;
      }
    }
        
    setOptionList((prevList) =>
      prevList?.map((item) =>
        item.itemNumber === option?.itemNumber && item.goodNumber === option?.goodNumber
          ? { ...option, count: newCount, orderPrice: newCount * option?.discountPrice }
          : item
      )
    );

  };

  const handleClear = () => {
    setOptionList((prevList) =>
      prevList?.filter((it) => it.itemNumber !== option.itemNumber || it.goodNumber !== option.goodNumber)
    );
  };

  const optionPrice = useMemo(() => {
    const internalOptionPrice = priceUtils.getDisplayPrice({
      priceFormat,
      price: Number(option.discountPrice * option.count).toLocaleString(),
      formattedPrice: Number(option.discountFormattedPrice * option.count).toLocaleString(),
    });

    return `${internalOptionPrice} ${priceFormat}`;
  }, [option.count, option.discountPrice, option.discountFormattedPrice]);
  const optionName = useMemo(
    () => (!isGroup && option.itemNumber === '00000' ? '' : isGroup ? option?.itemName : option?.itemNameView),
    [isGroup, option.itemName, option.itemNumber, option?.itemNameView]
  );
  const deletable = useMemo(
    () => isGroup || option.multiItem || option.additionalItem,
    [isGroup, option.multiItem, option.additionalItem]
  );
  const showStockQuantity = useMemo(() => {
    return option.itemNumber === '00000' && option.stockManagement && stockQtyDisplay;
  }, [option.itemNumber, option.stockManagement, stockQtyDisplay]);

  const showRental = useMemo(() => {
    return isRental && !option.additionalItem;
  }, [isRental, option.additionalItem]);

  const showRegularDelivery = useMemo(() => {
    return isRegularDelivery && !option.additionalItem;
  }, [isRegularDelivery, option.additionalItem]);

  return (
    <SelectedOptions>
      <TopWrapper>
        <FlexBox align-items={'flex-start'}>
          {option.subGoodIndex && (
            <>
              <TextBox
                weight={moTheme.font.weight.medium}
                size={moTheme.font.size.$12}
                color={theme.color.primary}
                style={{ minWidth: 'max-content' }}
              >
                {option.subGoodIndex}
              </TextBox>
              <Spacing right={3} />
            </>
          )}
          <TextBox
            weight={moTheme.font.weight.demiLight}
            size={moTheme.font.size.$12}
            style={{ wordBreak: 'break-all' }}
          >
            {optionName}
          </TextBox>
        </FlexBox>
        <Spacing left={6} />
        {deletable ? (
          <StyledSpacing aria-label={'선택 옵션 삭제'}>
            <ClearIcon onClick={handleClear} />
          </StyledSpacing>
        ) : (
          <FlexBox />
        )}
      </TopWrapper>
      <Spacing top={6} />
      <BottomWrapper>
        <FlexBox>
          <NumberIndicator
            value={option.count}
            minValue={1}
            disabled={!isPriceOpen}
            onChange={(e) => handleCountChange(e, option)}
          />
          <Spacing right={10} />
          {showStockQuantity && <TextBox>{`(재고 : ${option.salePossibleQuantity}개)`}</TextBox>}
        </FlexBox>
        {isPriceOpen && (
          <FlexBox>
            <TextBox weight={moTheme.font.weight.bold} size={moTheme.font.size.$14}>
              {optionPrice}
            </TextBox>
          </FlexBox>
        )}
      </BottomWrapper>
      {showRental && <OptionPriceUnitInfo title={'월 렌탈료'} />}
      {showRegularDelivery && <OptionPriceUnitInfo title={'1회당'} />}
    </SelectedOptions>
  );
};

GoodSelectedOption.propTypes = {
  option: PropTypes.object,
  isGroup: PropTypes.bool,
  isRental: PropTypes.bool,
  isRegularDelivery: PropTypes.bool,
  stockQtyDisplay: PropTypes.bool,
  showBtnBulkp: PropTypes.bool,
  selectedOptionList: PropTypes.array,
  ordPossibleMaxQtyStandardCode: PropTypes.string,
  ordPossibleMaxQty: PropTypes.number,
  ordPossibleMinQty: PropTypes.number,
  ordPossibleMaxAmount: PropTypes.number,
  ordPossibleMaxFormattedAmount: PropTypes.number,
  ordPossibleMinAmount: PropTypes.number,
  ordPossibleMinFormattedAmount: PropTypes.number,
  bulkpOrdPossibleMinQty: PropTypes.number
};

export default GoodSelectedOption;

const StyledSpacing = styled.button`
  ${overrideProperties(paddingPropTypes, {
    'padding-top': 0,
    'padding-right': 0,
    'padding-bottom': 0,
    'padding-left': 0,
  })}
  ${overrideProperties(sizePropTypes, {
    width: 'auto',
    height: 'auto',
  })}
    margin: ${({ margin }) => margin};
`;

const SelectedOptions = styled(FlexBoxColumn)`
  width: 100%;
  background-color: ${(props) => props.theme.color.background.basic};
  padding: 10px 14px 14px 14px;
`;

const TopWrapper = styled(FlexBox)`
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
`;

const BottomWrapper = styled(FlexBox)`
  align-items: flex-end;
  justify-content: space-between;
  flex-shrink: 0;
  width: 100%;
`;

const ClearIcon = styled(ClearSvg)`
  cursor: pointer;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
`;
